import { useTranslations } from '@vocab/react';
import { Alert, Text, RadioGroup, RadioItem } from 'braid-design-system';
import React, { forwardRef, useState, type FormEvent, type Ref } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  SALARY_HIDE,
  TOGGLE_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { getSalaryCurrency } from 'src/app/utils/salary/getSalary';
import { constructDefaultSalaryDisplayText } from 'src/app/utils/salary/utils/formatSalary';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateDisplayPay } from 'src/app/v1/state/modules/formData/displayPay';
import { actionUpdateSalaryText } from 'src/app/v1/state/modules/formData/salaryText';
import { actionUpdateTrackingDisplayPay } from 'src/app/v1/state/modules/tealium/displayPay';
import { PayDisplayText } from 'src/app/v1/views/components/PayDisplayText';
import type { AppState } from 'src/types/appState';

import { usePayType } from '../../hooks/usePayType';

import translations from './.vocab';
import { getPayDisplayApplicationPercentage } from './utils/getPayDisplayApplicationPercentage';

export const PayDisplayToggle = forwardRef((_props, ref: Ref<any>) => {
  const {
    hirerNation,
    currency,
    displayPay,
    jobLocationCountry,
    maxSalary,
    minSalary,
    salaryText,
  } = useSelector(
    (state: AppState) => ({
      hirerNation: state.session.hirerAccount.nation,
      currency: state.formData.salaryCurrency,
      displayPay: state.formData.displayPay,
      jobLocationCountry: state.session.jobLocationCountry,
      maxSalary: state.formData.maxSalary,
      minSalary: state.formData.minSalary,
      salaryText: state.formData.salaryText,
    }),
    shallowEqual,
  );

  const salaryCurrency = getSalaryCurrency({
    formDataCurrency: currency,
    hirerNation,
    jobLocationCountry,
  });

  const { t } = useTranslations(translations);
  const payType = usePayType();

  const dispatch = useDispatch();
  type PayDisplayToggleValue = 'show' | 'hide';
  const [payDisplayToggleValue, setPayDisplayToggleValue] =
    useState<PayDisplayToggleValue>(displayPay ? 'show' : 'hide');

  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setPayDisplayToggleValue(value as PayDisplayToggleValue);

    const newDisplayPay = value === 'show';

    dispatch(actionUpdateDisplayPay(newDisplayPay));
    dispatch(actionUpdateTrackingDisplayPay(newDisplayPay));

    if (newDisplayPay && !salaryText) {
      const salaryTextDetails = constructDefaultSalaryDisplayText({
        min: minSalary,
        max: maxSalary,
        currency: salaryCurrency,
        payType,
      });

      dispatch(actionUpdateSalaryText(salaryTextDetails));
    }

    const eventData = {
      eventName: TOGGLE_PRESSED,
      toggleType: SALARY_HIDE,
      toggleState: newDisplayPay ? 'off' : 'on',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  // Calculate the application percentage relevant to the country
  // the job is being posted into
  const applicationPercentage = getPayDisplayApplicationPercentage(
    jobLocationCountry || hirerNation || 'au',
  );
  const hasApplicationPercentage =
    typeof applicationPercentage === 'number' && applicationPercentage > 0;
  const payDisplayToggleAlertText = hasApplicationPercentage
    ? t('pay-display-toggle-alert-text-with-percentage', {
        percentage: applicationPercentage.toString(),
      })
    : t('pay-display-toggle-alert-text');

  return (
    <RadioGroup
      id="payDisplayToggle"
      value={payDisplayToggleValue}
      onChange={handleChange}
      label={t('pay-display-text-label')}
      secondaryLabel={t('pay-display-text-secondary-label')}
    >
      <RadioItem label={t('pay-display-toggle-show-label')} value="show">
        <PayDisplayText ref={ref} />
      </RadioItem>
      <RadioItem label={t('pay-display-toggle-hide-label')} value="hide">
        {!displayPay && (
          <Alert id="payDisplayToggleAlert" tone="caution">
            <Text>{payDisplayToggleAlertText}</Text>
          </Alert>
        )}
      </RadioItem>
    </RadioGroup>
  );
});
